import { useState, useCallback, useEffect } from 'react';

import { supabase } from '../../../lib/supabase';
import { GameRoom, GameSession, GamePlayer, GameAnswer, SubmitGameAnswerParams } from '../types/quiz';

export const useMultiplayerStore = () => {
  const [gracePeriodEnd, setGracePeriodEnd] = useState<number | null>(null);
  const [currentRoom, setCurrentRoom] = useState<GameRoom | null>(null);
  const [currentSession, setCurrentSession] = useState<GameSession | null>(null);
  // Patch for grace period: extend GamePlayer with finished_at for this file
  type GamePlayerWithFinish = GamePlayer & { finished_at?: string };
  const [players, setPlayers] = useState<GamePlayerWithFinish[]>([]);
  const [answers, setAnswers] = useState<GameAnswer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper: Check if grace period should start and set end time
  useEffect(() => {
    if (!players || players.length === 0) return;
    // Find finished players
    const finishedPlayers = players.filter(p => p.finished_at);
    // Only trigger when exactly 1 player is finished (first finisher)
    if (finishedPlayers.length === 1 && !gracePeriodEnd && finishedPlayers[0].finished_at) {
      const firstFinishTime = new Date(finishedPlayers[0].finished_at).getTime();
      const GRACE_PERIOD_SECONDS = 30; // adjust as needed
      setGracePeriodEnd(firstFinishTime + GRACE_PERIOD_SECONDS * 1000);
      // Optionally: Save gracePeriodEnd to Supabase for sync
      // (implement db sync if needed)
    }
  }, [players, gracePeriodEnd]);

  const fetchPlayers = useCallback(async (roomId: string) => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('multiplayer_game_room_players')
        .select('*')
        .eq('room_id', roomId);

      if (fetchError) throw fetchError;
      
      setPlayers(data || []);
      return data || [];
    } catch (err) {
      console.error('Error fetching players:', err);
      setError('Failed to fetch players');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const submitAnswer = useCallback(async (params: SubmitGameAnswerParams) => {
    try {
      setLoading(true);
      const { data, error: submitError } = await supabase
        .from('game_answers')
        .insert(params)
        .select()
        .single();

      if (submitError) throw submitError;
      
      return { data, error: null };
    } catch (err) {
      console.error('Error submitting answer:', err);
      setError('Failed to submit answer');
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  const listenRoom = useCallback((roomId: string, callback: (room: GameRoom) => void) => {
    const subscription = supabase
      .channel(`room:${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'multiplayer_game_rooms',
          filter: `id=eq.${roomId}`,
        },
        (payload: { new: GameRoom }) => {
          callback(payload.new as GameRoom);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const listenSession = useCallback((sessionId: string, callback: (session: GameSession) => void) => {
    const subscription = supabase
      .channel(`session:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'multiplayer_game_sessions',
          filter: `id=eq.${sessionId}`,
        },
        (payload: { new: GameSession }) => {
          callback(payload.new as GameSession);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const listenAnswers = useCallback((sessionId: string, callback: (answers: GameAnswer[]) => void) => {
    const subscription = supabase
      .channel(`session_answers:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'multiplayer_game_room_answers',
          filter: `room_id=eq.${sessionId}`,
        },
        async () => {
          const { data } = await supabase
            .from('multiplayer_game_room_answers')
            .select('*')
            .eq('room_id', sessionId);
          
          if (data) {
            callback(data);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const listenGracePeriodEnd = useCallback((sessionId: string, callback: (gracePeriodEnd: number | null) => void) => {
    // Locally extend GameSession with grace_period_end
    type GameSessionWithGrace = GameSession & { grace_period_end?: number | null };
    const subscription = supabase
      .channel(`session_grace_period:${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'multiplayer_game_sessions',
          filter: `id=eq.${sessionId}`,
        },
        (payload: { new: GameSession }) => {
          const newGracePeriodEnd = (payload.new as GameSessionWithGrace).grace_period_end;
          callback(newGracePeriodEnd ?? null);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const endGame = useCallback(async (sessionId: string) => {
    try {
      setLoading(true);
      const { data, error: endError } = await supabase
        .from('multiplayer_game_sessions')
        .update({ status: 'completed' })
        .eq('id', sessionId)
        .select()
        .single();

      if (endError) throw endError;
      
      return { data, error: null };
    } catch (err) {
      console.error('Error ending game:', err);
      setError('Failed to end game');
      return { data: null, error: err };
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    currentRoom,
    currentSession,
    players,
    answers,
    loading,
    error,
    gracePeriodEnd,
    setCurrentRoom,
    setCurrentSession,
    setPlayers,
    setAnswers,
    setGracePeriodEnd,
    fetchPlayers,
    submitAnswer,
    listenRoom,
    listenSession,
    listenAnswers,
    listenGracePeriodEnd,
    endGame,
  };
};

export default useMultiplayerStore;
